"use client";

import {
  CubeIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import WorkingHardPage from "../LoadingPages/WorkingHardPage";
import { useCallback, useEffect, useState } from "react";
import SiteAdmin from "./SiteAdmin";
import { useSelector } from "react-redux";
import UserManager from "./UserManager";
import { useLocation, useNavigate } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Settings() {
  const { adminActive, userAdmin } = useSelector((state) => state.admin);
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([
    {
      name: "General",
      id: 1,
      url: "/settings",
      icon: UserCircleIcon,
      current: true,
      component: WorkingHardPage,
      visible: true,
    },
    {
      name: "Site Admin",
      id: 2,
      url: "/settings/site-admin",
      icon: CubeIcon,
      current: false,
      component: SiteAdmin,
      visible: adminActive,
      adminBtn: adminActive,
    },
    {
      name: "User Admin",
      id: 3,
      url: "/settings/user-admin",
      icon: UsersIcon,
      current: false,
      component: UserManager,
      visible: userAdmin,
    },
  ]);

  // Sync tab selection with URL
  useEffect(() => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) => {
        if (tab.url === location.pathname) {
          return { ...tab, current: true };
        }
        // Default to General if on /settings root
        if (location.pathname === "/settings" && tab.id === 1) {
          return { ...tab, current: true };
        }
        return { ...tab, current: false };
      })
    );
  }, [location.pathname]);

  const updateProfileButtons = useCallback(() => {
    setTabs((prevButtons) =>
      prevButtons.map((btn) => {
        if (btn.adminBtn) {
          return {
            ...btn,
            visible: adminActive,
          };
        }
        return btn;
      })
    );
  }, [adminActive]);

  useEffect(() => {
    updateProfileButtons();
  }, [adminActive, updateProfileButtons]);

  const selectTab = (index) => {
    setTabs(
      tabs.map((tab) => {
        if (tab.id === index) {
          // Navigate to the tab's url
          navigate(tab.url);
          return {
            ...tab,
            current: true,
          };
        }
        return {
          ...tab,
          current: false,
        };
      })
    );
  };

  return (
    <>
      <div className="lg:flex mx-2 my-2">
        <aside className="flex overflow-x-auto border-gray-900/5 lg:block lg:w-64 lg:flex-none ">
          <nav className="flex-none px-4 sm:px-6 lg:px-0">
            <ul className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {tabs
                .filter((tab) => tab.visible)
                .map((item) => (
                  <li key={item.name}>
                    <div
                      onClick={() => selectTab(item.id)}
                      className={classNames(
                        item.current
                          ? "bg-gray-50 text-[var(--primary-color)]"
                          : "text-gray-700 hover:bg-gray-50 hover:text-[var(--secondary-color)]",
                        "group flex gap-x-3 rounded-md py-2 pr-3 pl-2 text-sm/6 font-semibold hover:cursor-pointer border-2"
                      )}
                    >
                      <item.icon
                        aria-hidden="true"
                        className={classNames(
                          item.current
                            ? "text-[var(--primary-color)]"
                            : "text-gray-400 group-hover:text-[var(--secondary-color)]",
                          "size-6 shrink-0"
                        )}
                      />
                      {item.name}
                    </div>
                  </li>
                ))}
            </ul>
          </nav>
        </aside>

        <main className="pr-4 lg:flex-auto px-2">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {tabs
              .filter((item) => item.current)
              .map((activeTab) => {
                return <activeTab.component />;
              })}
          </div>
        </main>
      </div>
    </>
  );
}
